// Copyright ©️ 2026 eVolve MEP, LLC

import { useActionState, useEffect, useMemo, useRef, useState } from 'react';

import {
  useEvolvePaginatedGet,
  useEvolvePatch,
  type CustomProperty,
  type EvolveApiParams,
  type Facility,
  type MachineElement,
  type Project,
  type WorkOrderItem,
  type WorkOrderPaginated,
} from '@evolvemep/api-ts';
import { Button, Flex, Group, LoadingOverlay, Menu, Space, Stack, Title } from '@mantine/core';
import { AgGridReact } from 'ag-grid-react';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { EditCustomPropertiesModal } from 'components/Mantine/CustomProperties/EditCustomPropertiesModal';
import { useCustomProperties } from 'components/Mantine/CustomProperties/useCustomProperties';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { getMultiRowSelection } from 'helpers/ag-grid/baseColumnDef';
import { AgGridEditable } from 'helpers/ag-grid/EditableCells/AgGridEditable';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { strongIsEqual } from 'helpers/strongEntries';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { AddElementsToWorkOrderPopover } from 'modules/Shop/EvolveFabrication/AddElementsToWorkOrderPopover';
import { BendCreatorModal } from 'modules/Shop/EvolveFabrication/BendCreator/BendCreatorModal';
import { EditBendModal } from 'modules/Shop/EvolveFabrication/BendCreator/EditBendModal';
import { EditBendElementDetailsModal } from 'modules/Shop/EvolveFabrication/BendElementDetails/EditBendElementDetailsModal';
import { ChangeBenderProfileModal } from 'modules/Shop/EvolveFabrication/ChangeBenderProfileModal';
import { getMachineElementsColumnDef } from 'modules/Shop/EvolveFabrication/columnDefs';
import { CutLists } from 'modules/Shop/EvolveFabrication/CutLists/CutLists';
import { DeletePartsModal } from 'modules/Shop/EvolveFabrication/DeletePartsModal';
import { RemoveElementsModal } from 'modules/Shop/EvolveFabrication/RemoveElementsModal';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';
import { isReleased } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

import { FabItemsGroupBySelect } from './FabItemsGroupBySelect';
import type { FabricationWorkType } from './machineElementActions/useDoUndoMachineElement';
import { useMachineElementActions } from './machineElementActions/useMachineElementActions';

type Props = {
  /** Called after elements are updated, deleted, or successfully added to/removed from a work order */
  onElementsUpdated?: () => void;
  /** If true, will render a button for the Bend Creator */
  includeBendCreator?: boolean;
  /** Bump this value to refresh the grid in place when a surface outside it has changed the rows' data. */
  refreshTrigger?: number;
} & (
  | {
      /** Used when viewing the fab items for a particular work order */
      workOrderPageOpts: {
        workOrder: WorkOrderPaginated;
        /** If provided, will only display fab items for this work order item */
        workOrderItem?: WorkOrderItem;
        hideHeader?: boolean;
      };
    }
  | {
      /** Used to filter the list of valid Work Orders when mapping items */
      selectedFacilities: Facility[];
      /** Used to filter the list of items in the grid, *and* filter the list of valid Work Orders when mapping items */
      selectedProjects: Project[];
      /** The pre-selected work order (if any) to use when mapping items */
      addToWorkOrder?: WorkOrderPaginated;
    }
);

const getDefaultOpts = (props: Props) => {
  const params: EvolveApiParams<'GET_PAGINATED', 'shop/fabrication/element'> =
    'workOrderPageOpts' in props
      ? {
          workOrderId: props.workOrderPageOpts.workOrder.workOrderId,
          workOrderItemId: props.workOrderPageOpts.workOrderItem?.workOrderItemId,
        }
      : {
          projectIds: props.selectedProjects.map((p) => p.projectId),
        };

  return { defaultConfig: { params } };
};

const LoadedFabricationItems = (props: Props & { customProperties: CustomProperty[] }) => {
  const { onElementsUpdated, includeBendCreator, refreshTrigger } = props;
  const { conduitTypes, conduitSizes } = useConduits();
  const navigate = useEvolveNavigate('Evolve Fabrication');
  const hasFabricationFullAccess = useHasAccess('fabrication', 'write');

  const isWorkOrderPage = 'workOrderPageOpts' in props;
  const isWorkOrderItemPage = isWorkOrderPage && isNotNil(props.workOrderPageOpts.workOrderItem);
  const [groupBy] = useLocalStorage('FABRICATION_GROUP_BY', 'none');

  const [defaultOpts, updateDefaultOpts] = useState(getDefaultOpts(props));

  useEffect(() => {
    const newDefaultOpts = getDefaultOpts(props);
    // Due to the way `defaultOpts` is defined,
    // we need to make sure we only update the reference if the actual options have changed
    updateDefaultOpts((o) => (strongIsEqual(o, newDefaultOpts) ? o : newDefaultOpts));
  }, [props]);

  const { fetchPage, abortController, setDefaultOpts } = useEvolvePaginatedGet('shop/fabrication/element', defaultOpts);

  useEffect(() => setDefaultOpts(defaultOpts), [defaultOpts, setDefaultOpts]);

  const [bendToAction, setBendToAction] = useState<{
    element: MachineElement;
    action: 'updateBendData' | 'updateElementData' | 'changeProfile';
  } | null>(null);

  const [elementsToRemove, setElementsToRemove] = useState<MachineElement[]>([]);

  const colDef = useMemo(
    () =>
      getMachineElementsColumnDef({
        conduitTypes,
        conduitSizes,
        onEditElementData: (element) => setBendToAction({ element, action: 'updateElementData' }),
        onEditBendData: (element) => setBendToAction({ element, action: 'updateBendData' }),
        onRemoveElement: (element) => setElementsToRemove([element]),
        onChangeBenderProfile: (element) => setBendToAction({ element, action: 'changeProfile' }),
        isWorkOrderPage,
        onElementUpdated: onElementsUpdated,
        customElementProperties: props.customProperties,
      }),
    [conduitTypes, conduitSizes, isWorkOrderPage, onElementsUpdated, props.customProperties],
  );

  const { agGridProps, gridApi } = useServerSideGrid({
    tableName: isWorkOrderPage ? 'work-order-elements' : 'machine-elements',
    tableViewType:
      'addToWorkOrder' in props
        ? TABLE_VIEW_TYPE.FABRICATION_ITEMS_MODAL
        : isWorkOrderPage
          ? TABLE_VIEW_TYPE.WORK_ORDER_FABRICATION_ITEMS
          : TABLE_VIEW_TYPE.PARTS,
    exportFileName: isWorkOrderPage ? props.workOrderPageOpts.workOrder.name.replace(/\s+/g, '-') : 'Fabrication Items',
    colDef,
    fetchPage,
    abortController,
    rowId: 'elementId',
  });

  const lastRefreshTrigger = useRef(refreshTrigger);
  useEffect(() => {
    if (lastRefreshTrigger.current !== refreshTrigger) {
      lastRefreshTrigger.current = refreshTrigger;
      refreshGrid(gridApi);
    }
  }, [refreshTrigger, gridApi]);

  const { markElementsDone, mutationRunning: doingItems } = useMachineElementActions();

  const [selectedElements, setSelectedElements] = useState<MachineElement[]>([]);

  const canMapToWorkOrder = !isWorkOrderPage && hasFabricationFullAccess;
  const canMarkItemsAsDone =
    isWorkOrderPage && isReleased(props.workOrderPageOpts.workOrder) && hasFabricationFullAccess;
  const canDeleteWorkOrderItems =
    isWorkOrderPage && !isWorkOrderItemPage && !props.workOrderPageOpts.hideHeader && hasFabricationFullAccess;
  const multiSelectEnabled = canMapToWorkOrder || canMarkItemsAsDone || canDeleteWorkOrderItems;

  const multiRowSelection = useMemo(
    () =>
      multiSelectEnabled
        ? getMultiRowSelection<MachineElement>({
            hideDisabledCheckboxes: true,
          })
        : undefined,
    [multiSelectEnabled],
  );

  const [cutListsKey, updateCutLists] = useActionState<number>((s) => s + 1, 0);

  const handleElementsUpdated = () => {
    refreshGrid(gridApi);
    updateCutLists();
    onElementsUpdated?.();
  };

  const hideHeader = isWorkOrderPage ? props.workOrderPageOpts.hideHeader : false;
  const { apiCall: patchPropertiesApiCall } = useEvolvePatch('shop/fabrication/element/:elementId');

  return (
    <>
      <Stack h="100%" w="100%">
        {!hideHeader && (
          <Flex justify="space-between" align="center" gap="md">
            {isWorkOrderPage ? (
              !isWorkOrderItemPage ? (
                <Title order={3} lineClamp={2}>
                  {props.workOrderPageOpts.workOrder.name}
                </Title>
              ) : (
                <Space />
              )
            ) : (
              includeBendCreator &&
              hasFabricationFullAccess && (
                <BendCreatorModal
                  onBendsCreated={handleElementsUpdated}
                  workOrder={props.addToWorkOrder}
                  selectedFacilities={props.selectedFacilities}
                  selectedProjects={props.selectedProjects}
                />
              )
            )}

            <Flex justify="flex-end" align="center" gap="md">
              {!isWorkOrderPage && <EditCustomPropertiesModal customPropertyTypeName="Fabrication Item" actionIcon />}
              <>
                {isWorkOrderPage && !isWorkOrderItemPage && !canMarkItemsAsDone && hasFabricationFullAccess && (
                  <BendCreatorModal
                    onBendsCreated={handleElementsUpdated}
                    workOrder={props.workOrderPageOpts.workOrder}
                  />
                )}
                {canMapToWorkOrder && (
                  <AddElementsToWorkOrderPopover
                    selectedElements={selectedElements}
                    onElementsAdded={handleElementsUpdated}
                    selectedFacilities={props.selectedFacilities}
                    selectedProjects={props.selectedProjects}
                    workOrder={props.addToWorkOrder}
                  />
                )}
                {isWorkOrderPage && !isWorkOrderItemPage && (
                  <>
                    <FabItemsGroupBySelect facilityId={props.workOrderPageOpts.workOrder.facilityId} />
                    <Button
                      variant="light"
                      onClick={() => {
                        void navigate(`/work-orders/${props.workOrderPageOpts.workOrder.workOrderId}`);
                      }}
                    >
                      View work order
                    </Button>
                    {groupBy === 'none' && hasFabricationFullAccess && (
                      <Button
                        variant="light"
                        disabled={selectedElements.length === 0}
                        onClick={() => setElementsToRemove(selectedElements)}
                      >
                        Remove from work order
                      </Button>
                    )}
                  </>
                )}
              </>

              {!isWorkOrderPage && hasFabricationFullAccess && (
                <DeletePartsModal selectedElements={selectedElements} onPartsDeleted={handleElementsUpdated} />
              )}
            </Flex>
          </Flex>
        )}

        {
          // The "Cut List" view is only valid when viewing a Work Order
          isWorkOrderPage && !isWorkOrderItemPage && groupBy === 'cut-lists' ? (
            <CutLists
              // CutLists fetches its own data, and each MachineCutList seeds its state from props,
              // so remount to pick up cut lists the backend reassigned after elements changed.
              key={cutListsKey}
              workOrderId={props.workOrderPageOpts.workOrder.workOrderId}
              canMarkItemsAsDone={canMarkItemsAsDone}
              customProperties={props.customProperties}
            />
          ) : (
            <Stack flex={1}>
              {isWorkOrderPage && (
                <Group justify="flex-end">
                  {canMarkItemsAsDone && (
                    <Menu>
                      <Menu.Target>
                        <Button
                          leftSection={<EvolveIcon icon="Check" />}
                          disabled={
                            selectedElements.length === 0 ||
                            !selectedElements.some((e) => e.canMarkBendDone || e.canMarkCutDone)
                          }
                          loading={doingItems}
                        >
                          Mark done
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {(['Bend', 'Cut'] as FabricationWorkType[]).map((workType) => (
                          <Menu.Item
                            value={workType}
                            key={workType}
                            disabled={
                              !selectedElements.some((e) =>
                                workType === 'Bend' ? e.canMarkBendDone : e.canMarkCutDone,
                              )
                            }
                            onClick={() => {
                              void markElementsDone(
                                selectedElements.filter((e) =>
                                  workType === 'Bend' ? e.canMarkBendDone : e.canMarkCutDone,
                                ),
                                workType,
                                () => {
                                  onElementsUpdated?.();
                                  refreshGrid(gridApi);
                                },
                              );
                            }}
                          >
                            Mark {workType.toLowerCase()} done
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  )}
                  {!isWorkOrderItemPage &&
                    !props.workOrderPageOpts.hideHeader &&
                    hasFabricationFullAccess &&
                    groupBy === 'none' && (
                      <DeletePartsModal
                        selectedElements={selectedElements}
                        onPartsDeleted={() => {
                          refreshGrid(gridApi);
                          handleElementsUpdated();
                        }}
                      />
                    )}
                </Group>
              )}
              <AgGridEditable
                gridApi={gridApi}
                patchCall={patchPropertiesApiCall}
                configMapper={(element) => ({ url: `shop/fabrication/element/${element.elementId}` })}
                requestConverter={(_element, { customProperties }) => ({
                  customProperties,
                })}
              >
                {(applyWrap) => (
                  <AgGridReact<MachineElement>
                    onFirstDataRendered={applyWrap}
                    {...agGridProps}
                    rowSelection={multiRowSelection}
                    onSelectionChanged={({ api }) => {
                      setSelectedElements(api.getSelectedRows());
                    }}
                  />
                )}
              </AgGridEditable>
            </Stack>
          )
        }

        {isWorkOrderPage && (
          <RemoveElementsModal
            elementsToRemove={elementsToRemove}
            workOrder={props.workOrderPageOpts.workOrder}
            onElementsRemoved={() => {
              handleElementsUpdated();
            }}
            onClose={() => setElementsToRemove([])}
          />
        )}
      </Stack>

      <EditBendModal
        element={bendToAction?.action === 'updateBendData' ? bendToAction.element : null}
        onClose={() => setBendToAction(null)}
        onBendUpdated={handleElementsUpdated}
      />
      <EditBendElementDetailsModal
        element={bendToAction?.action === 'updateElementData' ? bendToAction.element : null}
        onClose={() => setBendToAction(null)}
        onBendUpdated={handleElementsUpdated}
      />
      <ChangeBenderProfileModal
        element={bendToAction?.action === 'changeProfile' ? bendToAction.element : null}
        onClose={() => setBendToAction(null)}
        onProfileChanged={handleElementsUpdated}
      />
    </>
  );
};

/** Render gate: Wait for custom properties to be loaded before rendering the main component   */
export const FabricationItems = (props: Props) => {
  const { customProperties } = useCustomProperties('Fabrication Item');
  // This implicitly makes a Shop license required to view the fabrication items since loading custom properties requires a shop license.
  // As of now, fabrication users will always also have a shop license, but this will need changed if that ever stops being the case.
  if (isNil(customProperties)) {
    return <LoadingOverlay visible />;
  }
  return <LoadedFabricationItems {...props} customProperties={customProperties} />;
};
